import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, X } from 'lucide-react';
import { Z_LAYERS } from '../constants/zLayers';
import Button from './Button';
import { useUpgradeFlow } from '../contexts/UpgradeFlowContext';

interface PremiumFeatureDialogProps {
  isOpen: boolean;
  onClose: () => void;
  featureName: string;
}

export function PremiumFeatureDialog({ isOpen, onClose, featureName }: PremiumFeatureDialogProps) {
  const { startUpgrade } = useUpgradeFlow();

  const openPaymentPage = () => {
    // The global upgrade dialog (UpgradeFlowProvider) takes over from here.
    startUpgrade();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center"
      style={{ zIndex: Z_LAYERS.MODAL }}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-transparent-light dark:bg-transparent-dark backdrop-blur-sm rounded-2xl shadow-xl w-[500px] max-w-[90vw] flex flex-col border border-gray-200/50 dark:border-gray-700/50"
        role="dialog"
        aria-modal="true"
        aria-labelledby="premium-dialog-title"
        aria-describedby="premium-dialog-description"
      >
        {/* Header Section */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex justify-between items-center">
            <motion.h2
              id="premium-dialog-title"
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              className="text-2xl font-semibold text-gray-800 dark:text-white flex items-center"
            >
              <Sparkles className="w-5 h-5 text-indigo-500 mr-2" />
              Premium Feature
              <Sparkles className="w-5 h-5 text-indigo-500 ml-2" />
            </motion.h2>
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={onClose}
              className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              aria-label="Close premium feature dialog"
            >
              <X className="w-6 h-6" />
            </motion.button>
          </div>

          <p id="premium-dialog-description" className="text-gray-600 dark:text-gray-300 mt-4">
            The <span className="font-semibold">{featureName}</span> is a premium feature. Upgrade
            to premium to access this and other exclusive features.
          </p>
        </div>

        {/* Content Section */}
        <div className="p-6">
          <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-xl p-4 mb-6">
            <h3 className="text-lg font-medium text-indigo-700 dark:text-indigo-300 mb-2">
              Premium Benefits
            </h3>
            <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
              <li className="flex items-start">
                <Sparkles className="w-4 h-4 text-indigo-500 mr-2 mt-0.5" />
                <span>Full Screen View for a more immersive experience</span>
              </li>
              <li className="flex items-start">
                <Sparkles className="w-4 h-4 text-indigo-500 mr-2 mt-0.5" />
                <span>Cloud Sync to access your prompts across devices</span>
              </li>
              <li className="flex items-start">
                <Sparkles className="w-4 h-4 text-indigo-500 mr-2 mt-0.5" />
                <span>Variable History to reuse previous inputs</span>
              </li>
              <li className="flex items-start">
                <Sparkles className="w-4 h-4 text-indigo-500 mr-2 mt-0.5" />
                <span>Custom Folder Colors and Icons</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Button Section */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-700">
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={onClose} type="button">
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={openPaymentPage}
              type="button"
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Upgrade to Premium
            </Button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
